import classNames from 'classnames'
import { format } from 'date-fns'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TFunction, useTranslation } from 'react-i18next'

import { AccessTimeFilled, AutoStories, NavigateBefore, NavigateNext, Error } from '@mui/icons-material'
import { Timeline, TimelineConnector, TimelineContent, TimelineItem, TimelineSeparator } from '@mui/lab'
import { Alert, Box, Button, CardContent, Chip, Divider, Pagination, Typography } from '@mui/material'
import { Card } from '@mui/material'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { HtmlContentParser } from '../../../../components/HtmlContentParser/HtmlContentParser'
import { ModalMode, liveEventReviewModalParserOptions } from '../../../../components/LiveEventModalLink/LiveEventModalLink'
import { Ribbon } from '../../../../components/Ribbon/Ribbon'
import { htmlDecode } from '../../../../helpers/dom'
import { shadeColor } from '../../../../helpers/shadeColor'
import { TranslationMap } from '../../../../types/TranslationMap'
import { useCurrentUserLanguage } from '../../../account/hooks/useCurrentUserLanguage'
import { useLiveEventTagGroupsMapByEventTypeId } from '../../hooks/useLiveEventTagGroups'
import { LiveEventEvolutionEntry } from '../../types/LiveEventCollection'
import { LiveEventsCalendarTimelineType, TrackingEventTimelineItem } from '../../types/LiveEvents'
import { TrackedGame } from '../../types/TrackedGame'
import { TrackingEvent } from '../../types/TrackingEvents'
import { LiveEventEvolutionTypeChip } from '../LiveEventEvolutionTypeChip/LiveEventEvolutionTypeChip'
import { LiveEventInfoTooltipContent } from '../LiveEventsCalendar/LiveEventsCalendarItem/LiveEventsCalendarTrackingEventItem/LiveEventInfoTooltipContent'
import './LiveEventEvolutionTimeline.scss'
import { LiveEventEvolutionTimelineSkeleton } from './LiveEventEvolutionTimelineSkeleton'

// Constants
const MAX_DESCRIPTION_LENGTH = 300
const AVERAGE_DAYS_PER_MONTH = 30.44
const defaultTimelineItemBGColor = '#273143'
const inactiveEventColor = '#b13d37'
const ITEMS_PER_PAGE = 100

interface LiveEventEvolutionTimelineProps {
  currentTrackedGame?: TrackedGame
  currentEventId: string | undefined
  events: { [eventId: string]: TrackingEvent }
  entries: LiveEventEvolutionEntry[]
  onEventSelect: (event: TrackingEvent) => void
  loading?: boolean
  onScrollParentTop?: () => void
}

// Helper function to get time difference label between two timestamps
const getTimeDifferenceLabel = (timestamp1: number, timestamp2: number, t: TFunction): string | undefined => {
  const daysDiff = Math.abs(Math.floor((timestamp2 - timestamp1) / (1000 * 60 * 60 * 24)))

  if (daysDiff === 0) {
    return undefined
  }

  // years
  if (daysDiff >= 365) {
    const yearsDiff = daysDiff / 365
    const yearsFixed = parseFloat(yearsDiff.toFixed(1))
    return t('live-events:year', { count: yearsFixed })
  }

  // months
  if (daysDiff >= AVERAGE_DAYS_PER_MONTH) {
    const monthsDiff = daysDiff / AVERAGE_DAYS_PER_MONTH
    const monthsFixed = parseFloat(monthsDiff.toFixed(1))
    return t('live-events:month', { count: monthsFixed })
  }

  // days
  return t('live-events:day', { count: daysDiff })
}

// Helper function to check if there's a valid description
const hasDescription = (descriptions: TranslationMap | undefined): boolean => {
  return typeof descriptions?.en === 'string' && descriptions.en.trim().length > 0
}

export const LiveEventEvolutionTimeline: React.FC<LiveEventEvolutionTimelineProps> = ({
  entries,
  onEventSelect,
  currentTrackedGame,
  events,
  currentEventId,
  loading = false,
  onScrollParentTop,
}) => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  const liveEventTagGroupsMapByEventTypeId = useLiveEventTagGroupsMapByEventTypeId()
  const [isHoveringChip, setIsHoveringChip] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const isInitialMount = useRef(true)

  // Helper function to check if a number is a valid date
  const isValidDate = useCallback((timestamp: number): boolean => {
    const date = new Date(timestamp)
    return !isNaN(date.getTime()) && date.getTime() > 0
  }, [])

  const sortedEntries = useMemo(() => {
    if (entries.length === 0) return []

    // Filter out entries that are in the future
    const now = Date.now()
    const filteredEntries = entries.filter((entry) => entry.start <= now)

    // Sort entries once from latest to oldest
    const sortedEntries = [...filteredEntries].sort((a, b) => b.start - a.start)

    return sortedEntries
  }, [entries])

  // Pagination logic
  const shouldShowPagination = sortedEntries.length > ITEMS_PER_PAGE
  const totalPages = Math.ceil(sortedEntries.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedEntries = shouldShowPagination ? sortedEntries.slice(startIndex, endIndex) : sortedEntries

  // Reset to page 1 when entries change
  useEffect(() => {
    setCurrentPage(1)
  }, [entries])

  // Scroll to top when page changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    if (onScrollParentTop) {
      onScrollParentTop()
    }
  }, [currentPage, onScrollParentTop])

  const getEvolutionTimelineItem = useCallback(
    (entry: LiveEventEvolutionEntry): TrackingEventTimelineItem => {
      const trackingEvent = events[entry.eventId]
      return {
        trackingEventId: entry.eventId,
        trackedGame: currentTrackedGame,
        trackingEvent: trackingEvent,
        type: LiveEventsCalendarTimelineType.TrackingEvent,
        highlighted: false,
        new: false,
        tagGroup: trackingEvent?.typeId ? liveEventTagGroupsMapByEventTypeId[trackingEvent.typeId] : undefined,
      } as TrackingEventTimelineItem
    },
    [currentTrackedGame, events, liveEventTagGroupsMapByEventTypeId]
  )

  const getDescriptionRawHtml = useCallback(
    (entry: LiveEventEvolutionEntry | TrackingEvent): string | null | undefined => {
      const trackingEvent = events[entry.eventId]

      if (!trackingEvent) {
        return null
      }

      const descriptionHTML = trackingEvent.comment?.content?.comment?.[userLanguage]
        ? trackingEvent.comment?.content?.comment?.[userLanguage]
        : trackingEvent.comment?.content?.comment?.['en']

      if (descriptionHTML) {
        const paragraphs = descriptionHTML.split('</p>')

        // find the first paragraph with content
        const paragraph = paragraphs.find((paragraph) => {
          return !!paragraph.replace(/<\/?[^>]+(>|$)/g, '')
        })

        const strippedParagraph = paragraph?.replace(/<\/?[^>]+(>|$)/g, '') || ''
        const decodedParagraph = htmlDecode(strippedParagraph)

        if (decodedParagraph && decodedParagraph.length > MAX_DESCRIPTION_LENGTH) {
          return decodedParagraph.substring(0, MAX_DESCRIPTION_LENGTH) + '...'
        } else {
          return decodedParagraph
        }
      }
    },
    [events, userLanguage]
  )

  const getBgColor = useCallback(
    (entry: LiveEventEvolutionEntry | TrackingEvent): string => {
      const trackingEvent = events[entry.eventId]
      const tagGroup = trackingEvent?.typeId ? liveEventTagGroupsMapByEventTypeId[trackingEvent.typeId] : undefined
      const newItem = entry.highlighted && entry.firstInstance

      if (newItem && tagGroup && tagGroup.colorHex) {
        return shadeColor(tagGroup.colorHex, -50)
      } else if (tagGroup && tagGroup.colorHex) {
        return tagGroup.colorHex
      } else {
        return defaultTimelineItemBGColor
      }
    },
    [events, liveEventTagGroupsMapByEventTypeId]
  )

  // Show skeleton when loading
  if (loading) {
    return <LiveEventEvolutionTimelineSkeleton />
  }

  if (!loading && (!currentTrackedGame || entries.length === 0)) {
    return (
      <Alert severity="info" sx={{ marginTop: 2 }}>
        {t('live-events:live_event_evolution_no_data_available')}
      </Alert>
    )
  }

  return (
    <>
      <Timeline position="alternate" className="LiveEventEvolutionTimeline">
        <TimelineItem>
          <TimelineSeparator className="LiveEventEvolutionTimelineSeparator">
            {currentPage === 1 && (
              <Chip
                label={t('live-events:present')}
                color="primary"
                sx={{ alignSelf: 'center' }}
                icon={<AccessTimeFilled sx={{ color: 'white', fontSize: '16px' }} />}
              />
            )}
            {currentPage > 1 && (
              <Chip
                label={`${t('common:label_page')} ${currentPage}`}
                color="primary"
                sx={{ alignSelf: 'center' }}
                className="LiveEventEvolutionTimelinePageChip"
                icon={<AutoStories sx={{ color: 'white', fontSize: '12px', marginLeft: '8px' }} />}
              />
            )}
            {paginatedEntries.length > 0 && isValidDate(paginatedEntries[0].start) && (
              <>
                <TimelineConnector sx={{ minHeight: '20px' }} />
                {(() => {
                  // For page 1, compare to present (Date.now())
                  // For other pages, compare to the last entry of the previous page
                  const referenceTimestamp = currentPage === 1 ? Date.now() : sortedEntries[(currentPage - 1) * ITEMS_PER_PAGE - 1]?.start

                  if (!referenceTimestamp || !isValidDate(referenceTimestamp)) {
                    return null
                  }

                  const timeLabel = getTimeDifferenceLabel(paginatedEntries[0].start, referenceTimestamp, t)
                  return timeLabel ? (
                    <Typography variant="caption" color="text.secondary" sx={{ marginTop: '2px', marginBottom: '2px', minWidth: '100px', textAlign: 'center' }}>
                      {timeLabel}
                    </Typography>
                  ) : null
                })()}
              </>
            )}
            <TimelineConnector sx={{ minHeight: '20px' }} />
          </TimelineSeparator>
          <TimelineContent></TimelineContent>
        </TimelineItem>

        {paginatedEntries.length === 0 && (
          <TimelineItem>
            <TimelineSeparator className="LiveEventEvolutionTimelineSeparator">
              <Chip label={t('live-events:live_event_evolution_no_entries')} color="error" icon={<Error sx={{ color: 'white', fontSize: '16px' }} />} />
            </TimelineSeparator>
            <TimelineContent></TimelineContent>
          </TimelineItem>
        )}

        {/* PAST EVENTS */}
        {paginatedEntries.map((entry, index) => {
          const side = index % 2 === 0 ? 'right' : 'left'

          return (
            <TimelineItem key={`${entry.eventId}-${entry.start}`} onMouseLeave={() => setIsHoveringChip(false)} sx={{ width: '100%' }}>
              {index === paginatedEntries.length - 1 ? (
                <TimelineSeparator className="LiveEventEvolutionTimelineSeparator">
                  {isValidDate(entry.start) && <Chip label={format(entry.start, 'MMM dd, yyyy')} color="primary" />}
                </TimelineSeparator>
              ) : (
                <TimelineSeparator className="LiveEventEvolutionTimelineSeparator">
                  {isValidDate(entry.start) && <Chip label={format(entry.start, 'MMM dd, yyyy')} color="primary" />}
                  <TimelineConnector sx={{ minHeight: '20px' }} />
                  {index < paginatedEntries.length - 1 &&
                    isValidDate(entry.start) &&
                    isValidDate(paginatedEntries[index + 1].start) &&
                    (() => {
                      const timeLabel = getTimeDifferenceLabel(entry.start, paginatedEntries[index + 1].start, t)
                      return timeLabel ? (
                        <Typography variant="caption" color="text.secondary" sx={{ marginTop: '2px', marginBottom: '2px' }}>
                          {timeLabel}
                        </Typography>
                      ) : null
                    })()}
                  <TimelineConnector sx={{ minHeight: '20px' }} />
                </TimelineSeparator>
              )}
              <TimelineContent>
                <GRTooltip
                  content={
                    <LiveEventInfoTooltipContent
                      item={getEvolutionTimelineItem(entry)}
                      itemContext={{ title: entry.name }}
                      descriptionRawHtml={getDescriptionRawHtml(entry)}
                      bgColor={getBgColor(entry)}
                      newItem={false}
                      mechanicChange={entry.mechanicChange}
                      firstInstance={entry.firstInstance}
                      inactive={events[entry.eventId]?.active === false}
                    />
                  }
                  popoverBorderColor={getBgColor(entry)}
                  disableMaxWidth={true}
                  hidden={isHoveringChip}
                  tooltipProps={{
                    // placement: side === 'right' ? 'left' : 'right',
                    PopperProps: {
                      modifiers: [
                        {
                          name: 'flip',
                          enabled: true,
                          options: {
                            fallbackPlacements: ['top', 'bottom', 'right', 'left'],
                          },
                        },
                        {
                          name: 'preventOverflow',
                          enabled: true,
                          options: {
                            boundary: 'viewport',
                          },
                        },
                      ],
                    },
                  }}
                >
                  <>
                    <Box
                      sx={{
                        borderRadius: '20px',

                        ...(events[entry.eventId]?.active === false && {
                          borderTop: `4px solid ${inactiveEventColor}`,
                          backgroundColor: inactiveEventColor,
                        }),
                      }}
                    >
                      <Card
                        className={classNames('LiveEventEvolutionTimelineCard', {
                          LiveEventEvolutionTimelineCard_highlighted: entry.firstInstance,
                        })}
                        onClick={() => {
                          const event = events[`${entry.eventId}-${entry.start}`] || events[entry.eventId]
                          if (event) onEventSelect(event)
                        }}
                        sx={{
                          position: 'relative',

                          ...(entry.highlighted && { borderColor: getBgColor(entry) }),
                        }}
                      >
                        {entry.eventId === currentEventId && (
                          <Ribbon variant="secondary" position={side === 'right' ? 'top-left' : 'top-right'} size="small">
                            <Typography variant="subtitle2" fontWeight="bold" fontSize={10}>
                              {t('common:selected')}
                            </Typography>
                          </Ribbon>
                        )}
                        <CardContent
                          sx={{
                            display: 'flex',
                            flexDirection: side === 'right' ? 'row-reverse' : 'row',
                            minHeight: '35px',
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: side === 'right' ? 'row' : 'row-reverse', alignItems: 'center', gap: 0.8 }}>
                            <Typography variant="h3" className="LiveEventEvolutionTimelineName">
                              {entry.name}
                            </Typography>

                            {entry.types?.map((type) => (
                              <Box key={type} onMouseEnter={() => setIsHoveringChip(true)} onMouseLeave={() => setIsHoveringChip(false)}>
                                <LiveEventEvolutionTypeChip type={type} colorHex={getBgColor(entry)} />
                              </Box>
                            ))}
                          </Box>
                        </CardContent>
                        {hasDescription(entry.descriptions) && (
                          <>
                            <Divider />
                            <Box sx={{ paddingBottom: 1, paddingLeft: 1, paddingRight: 1, paddingTop: 1, textAlign: 'left' }}>
                              <Box sx={{ paddingLeft: 1, paddingRight: 1, paddingBottom: 1, paddingTop: 0.5 }}>
                                <Typography variant="body2">
                                  <HtmlContentParser
                                    rawHtml={entry.descriptions[userLanguage] || entry.descriptions.en}
                                    parserOptions={liveEventReviewModalParserOptions(currentTrackedGame?.game.id || '', {
                                      liveEventModalMode: ModalMode.Replace,
                                    })}
                                  />
                                </Typography>
                              </Box>
                            </Box>
                          </>
                        )}
                      </Card>
                    </Box>
                  </>
                </GRTooltip>
              </TimelineContent>
            </TimelineItem>
          )
        })}
      </Timeline>

      {/* PAGINATION CONTROLS */}
      {shouldShowPagination && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Button
            variant="outlined"
            startIcon={<NavigateBefore />}
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            {t('common:label_previous')}
          </Button>

          <Pagination count={totalPages} page={currentPage} onChange={(_, page) => setCurrentPage(page)} color="primary" showFirstButton showLastButton />

          <Button
            variant="outlined"
            endIcon={<NavigateNext />}
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            {t('common:label_next')}
          </Button>
        </Box>
      )}
    </>
  )
}

export default LiveEventEvolutionTimeline
